import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export default function LiveChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, sender: 'bot', text: "Hi there! Welcome to OutsourcedHub. How can we help you scale today?" },
  ]);
  const [inputMessage, setInputMessage] = useState('');

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    const userMsg = { id: Date.now(), sender: 'user', text: inputMessage };
    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    setTimeout(() => {
      setMessages((prev) => [...prev, {
        id: Date.now() + 1,
        sender: 'bot',
        text: "Thanks for reaching out! Our team will reply shortly. Meanwhile, feel free to book a free consultation and we'll match you with the perfect VA.",
      }]);
    }, 900);
  };

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center text-[#04120C] ${isOpen ? '' : 'animate-emerald-pulse'}`}
        data-testid="live-chat-toggle"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-7 h-7" />}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[500px] bg-[#0D1612] rounded-3xl shadow-2xl border border-emerald-500/20 flex flex-col overflow-hidden"
            data-testid="live-chat-window"
          >
            <div className="bg-emerald-500 p-5">
              <h3 className="text-[#04120C] font-display font-bold text-lg">Live Chat Support</h3>
              <p className="text-[#04120C]/70 text-sm">Typically replies in minutes</p>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                    m.sender === 'user' ? 'bg-emerald-500 text-[#04120C] font-medium' : 'bg-white/[0.06] text-slate-200'
                  }`}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleSend} className="p-4 border-t border-white/[0.06]">
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Type your message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  className="flex-1 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-emerald-500"
                  data-testid="live-chat-input"
                />
                <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-[#04120C]" data-testid="live-chat-send">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
